import React, { useState, useRef, useEffect, useMemo } from 'react';
import { Upload, FileImage, AlertCircle, CheckCircle, Mail, User, Info, ExternalLink, Lock, Shield, UserX, Brain, Copy, Check, Sparkles, Heart, Briefcase } from 'lucide-react';
import { useAuth } from '../hooks/useAuth';
import { showToast, handleApiError, RateLimitInfo } from '../utils/errorHandler';
import { Link } from 'react-router-dom';
import FileUpload from '../components/CVUpload/FileUpload';
import CVEditor from '../components/CVUpload/CVEditor';
import AnonymizationConfirmModal from '../components/CVUpload/AnonymizationConfirmModal';
import UploadSuccessModal from '../components/CVUpload/UploadSuccessModal';
import AuthModal from '../components/Auth/AuthModal';
import { useAuthModal } from '../hooks/useAuthModal';
import { useTranslation } from 'react-i18next';
import RateLimitNotification from '../components/RateLimitNotification';
import FeatureBlockModal from '../components/FeatureBlockModal';

// Feature flag - set to true to re-enable Vee Helper
interface UploadedFile {
  file: File;
  url: string;
  type: 'pdf' | 'png';
}

// Detect in-app browsers (TikTok, Instagram, Facebook, etc.)
const detectInAppBrowser = (): string | null => {
  const ua = navigator.userAgent || navigator.vendor || (window as any).opera || '';
  // TikTok uses "musical_ly" (old name) and "BytedanceWebview"
  if (/musical[_.]?ly|BytedanceWebview|TikTok/i.test(ua)) return 'TikTok';
  if (/Instagram/i.test(ua)) return 'Instagram';
  if (/FBAN|FBAV/i.test(ua)) return 'Facebook';
  if (/LinkedIn/i.test(ua)) return 'LinkedIn';
  if (/Snapchat/i.test(ua)) return 'Snapchat';
  if (/Twitter/i.test(ua)) return 'Twitter';
  return null;
};

interface CVUploadProps {
  /** Render inside a modal/panel: drops the full-page chrome (header, in-app banner). */
  embedded?: boolean;
  /** Hide contact + job-preferences in the editor (prefs live on the Passport prefs page). */
  hidePreferences?: boolean;
  /** Called with the new cv_id once the upload is queued. */
  onUploaded?: (cvId: string) => void;
}

const CVUpload: React.FC<CVUploadProps> = ({ embedded = false, hidePreferences = false, onUploaded }) => {
  const { t, i18n } = useTranslation('inspire');
  const { user, getAuthHeaders, isLoading, apiCall } = useAuth();
  const { showAuthModal, setShowAuthModal, requireAuth, isSignedIn } = useAuthModal();
  const [uploadedFile, setUploadedFile] = useState<UploadedFile | null>(null);
  const [convertedPages, setConvertedPages] = useState<string[]>([]);
  const [anonymizedPages, setAnonymizedPages] = useState<string[]>([]);
  const [currentPageIndex, setCurrentPageIndex] = useState(0);
  const [isProcessing, setIsProcessing] = useState(false);
  const [uploadStatus, setUploadStatus] = useState<'idle' | 'uploading' | 'queued' | 'success' | 'error'>('idle');
  const [taskInfo, setTaskInfo] = useState<{cv_id?: string, task_id?: string, message?: string}>({});
  const statusRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [cvMetadata, setCvMetadata] = useState<{
    career_status?: string;
    base_locations?: string;
    preferred_locations?: string;
    role_preferences?: string;
    current_role?: string;
    current_level?: string;
    is_contactable?: boolean;
  }>({ career_status: 'invisible', is_contactable: false });
  const [profileLoaded, setProfileLoaded] = useState(false);
  const [existingCvId, setExistingCvId] = useState<string | null>(null);
  const [dragActive, setDragActive] = useState(false);
  const [showAnonymizationReminder, setShowAnonymizationReminder] = useState(false);
  const [rateLimitInfo, setRateLimitInfo] = useState<RateLimitInfo | null>(null);
  const [isBlocked, setIsBlocked] = useState(false);
  const [showBlockModal, setShowBlockModal] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [showUploadSuccessModal, setShowUploadSuccessModal] = useState(false);
  const [pendingUpload, setPendingUpload] = useState<{
    images: string[];
    consent: boolean;
  } | null>(null);
  const [urlCopied, setUrlCopied] = useState(false);

  // Detect in-app browser once on mount
  const inAppBrowser = useMemo(() => detectInAppBrowser(), []);

  const handleCopyUrl = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
      setUrlCopied(true);
      setTimeout(() => setUrlCopied(false), 2000);
    } catch (err) {
      // Fallback for older browsers
      const textArea = document.createElement('textarea');
      textArea.value = window.location.href;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      setUrlCopied(true);
      setTimeout(() => setUrlCopied(false), 2000);
    }
  };

  // Record consent for GDPR compliance
  const recordConsent = async (consentType: string, granted: boolean) => {
    if (!user?.userId) {
      console.warn('Cannot record consent: user not authenticated');
      return;
    }

    try {
      const response = await apiCall(
        `${process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000'}/api/v1/profiles/by-uuid/${user.userId}/consents`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            consent_type: consentType,
            granted: granted,
            version: '1.0'
          })
        }
      );

      if (!response.ok) {
        throw new Error(`Failed to record consent: ${consentType}`);
      }

      console.debug(`✅ Consent recorded: ${consentType}`);
    } catch (error) {
      console.error(`❌ Failed to record consent: ${consentType}`, error);
      throw error;
    }
  };

  // Auth prompt is now shown only when user tries to select a file (not on page load)

  // Load existing profile data when user is authenticated (only once)
  useEffect(() => {
    const loadProfileSettings = async () => {
      if (!isSignedIn || !user || profileLoaded) {
        return;
      }

      try {
        console.debug('🔄 Loading existing profile settings...');
        const response = await apiCall(`${process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000'}/api/v1/profiles/${user.userId}`);

        if (response.ok) {
          const profile = await response.json();
          console.debug('✅ Profile loaded:', profile);

          // Block recruiters from accessing CV Upload
          if (profile.is_recruiter) {
            setIsBlocked(true);
            setShowBlockModal(true);
            return;
          }

          // Check if user already has a CV uploaded
          if (profile.cv_id) {
            setExistingCvId(profile.cv_id);
            console.debug('📄 User has existing CV:', profile.cv_id);
          }

          // Always preload on first load - user hasn't had chance to type yet
          const preloadedData = {
            career_status: profile.career_status || 'invisible',
            base_locations: profile.base_locations || '',
            preferred_locations: profile.preferred_locations || '',
            role_preferences: profile.role_preferences || '',
            current_role: profile.current_role || '',
            current_level: profile.current_level || '',
            is_contactable: profile.is_contactable || false
          };

          setCvMetadata(preloadedData);
          console.debug('🎯 Profile settings preloaded successfully');
          console.debug('🔍 Preloaded data details:', preloadedData);

          setProfileLoaded(true);
          console.debug('✅ profileLoaded set to true');
        } else if (response.status === 404) {
          // No profile exists yet, use defaults
          console.debug('ℹ️ No existing profile found, using defaults');
          setProfileLoaded(true);
          console.debug('✅ profileLoaded set to true');
        } else {
          console.error('❌ Failed to load profile:', response.statusText);
          setProfileLoaded(true);
          console.debug('✅ profileLoaded set to true');
        }
      } catch (error) {
        console.error('❌ Error loading profile:', error);
        setProfileLoaded(true);
      }
    };

    loadProfileSettings();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSignedIn, user?.userId]);

  // Auto-scroll to status message when upload status changes (only for success/error, not queued - modal handles that)
  useEffect(() => {
    if (uploadStatus === 'success' || uploadStatus === 'error') {
      statusRef.current?.scrollIntoView({ 
        behavior: 'smooth', 
        block: 'center' 
      });
    }
  }, [uploadStatus]);

  const handleFileSelect = (file: File) => {
    // Auth check is now handled in FileUpload component (on button click)
    const url = URL.createObjectURL(file);
    const type = file.type === 'application/pdf' ? 'pdf' : 'png';
    
    setUploadedFile({ file, url, type });
    setConvertedPages([]);
    setCurrentPageIndex(0);
    setUploadStatus('idle');
  };

  const handleAuthRequired = () => {
    setShowAuthModal(true);
  };

  // Drag & Drop handlers for the placeholder area
  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    // Check if recruiter trying to drop file
    if (isBlocked) {
      setShowBlockModal(true);
      return;
    }

    // Check auth before processing dropped files
    requireAuth(() => {
      const files = Array.from(e.dataTransfer.files);
      if (files.length > 0) {
        handleFileSelect(files[0]);
      }
    });
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDragEnter = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(true);
  };

  const handleDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
  };

  const handlePagesConverted = (pages: string[]) => {
    setConvertedPages(pages);
    setCurrentPageIndex(0);
    
    // Show reminder highlight when pages are loaded
    if (pages.length > 0) {
      setShowAnonymizationReminder(true);
    }
  };

  // Check for expired upload lock on mount
  useEffect(() => {
    const uploadTimestamp = localStorage.getItem('cv_upload_timestamp');
    if (uploadTimestamp) {
      const elapsed = Date.now() - parseInt(uploadTimestamp);
      const cooldownMs = 6 * 60 * 1000;
      
      if (elapsed < cooldownMs) {
        setIsProcessing(true);
        setUploadStatus('queued');
        console.debug('⏳ Previous upload still in progress (within cooldown window)');
      } else {
        // Expired - cleanup
        localStorage.removeItem('cv_upload_timestamp');
        console.debug('✅ Previous upload lock expired, cleared');
      }
    }
  }, []);

  const handleUploadAnonymized = async (anonymizedImages: string[], consentGiven: boolean) => {
    // Debug auth state
    console.debug('🔍 Auth Debug:', { 
      isLoading, 
      isSignedIn, 
      hasUser: !!user,
      userEmail: user?.email,
      showAuthModal 
    });
    
    // Auth check FIRST
    if (isLoading || !isSignedIn || !user) {
      console.debug('❌ User not authenticated, showing auth prompt');
      if (!isSignedIn) {
        setShowAuthModal(true);
      }
      return;
    }
    
    // Check if recruiter trying to upload
    if (isBlocked) {
      setShowBlockModal(true);
      return;
    }
    
    console.debug('✅ User authenticated, proceeding with anonymization check');
    console.debug('✅ Consent given:', consentGiven);

    // Show confirmation modal before uploading
    setPendingUpload({ images: anonymizedImages, consent: consentGiven });
    setShowConfirmModal(true);
  };

  const confirmAndUpload = async () => {
    if (!pendingUpload) return;

    const { images: anonymizedImages, consent: consentGiven } = pendingUpload;
    
    // Close modal
    setShowConfirmModal(false);
    setPendingUpload(null);
    
    console.debug('✅ User confirmed anonymization, proceeding with upload');

    const uploadTimestamp = localStorage.getItem('cv_upload_timestamp');
    if (uploadTimestamp) {
      const elapsed = Date.now() - parseInt(uploadTimestamp);
      const cooldownMs = 6 * 60 * 1000;
      
      if (elapsed < cooldownMs) {
        console.debug('⚠️ Upload already in progress, ignoring duplicate request');
        showToast(t('errors.uploadInProgress'), 'info');
        return;
      } else {
        localStorage.removeItem('cv_upload_timestamp');
      }
    }

    setIsProcessing(true);
    setUploadStatus('uploading');

    try {
      // Note: Old CV cleanup is handled automatically by backend
      // Backend checks if user has existing cv_id and deletes before processing new upload
      
      // Convert base64 to blobs
      const formData = new FormData();
      
      for (let i = 0; i < anonymizedImages.length; i++) {
        const base64 = anonymizedImages[i];
        const response = await fetch(base64);
        const blob = await response.blob();
        const file = new File([blob], `page${i + 1}.png`, { type: 'image/png' });
        
        if (i === 0) {
          formData.append('page1', file);
        } else if (i === 1) {
          formData.append('page2', file);
        }
      }

      // Add metadata fields (career_status is required)
      formData.append('career_status', cvMetadata.career_status || 'invisible');
      // Always include these fields (even if empty) to allow clearing
      formData.append('base_locations', cvMetadata.base_locations?.trim() || '');
      formData.append('preferred_locations', cvMetadata.preferred_locations?.trim() || '');
      formData.append('role_preferences', cvMetadata.role_preferences?.trim() || '');
      formData.append('current_role', cvMetadata.current_role?.trim() || '');
      formData.append('current_level', cvMetadata.current_level || '');
      formData.append('is_contactable', String(cvMetadata.is_contactable || false));
      
      // Add GDPR consent flag (required)
      formData.append('consent_given', String(consentGiven));

      console.debug('🔍 CVUpload.tsx - Sending formData:');
      console.debug('🔍 CVUpload.tsx - career_status:', cvMetadata.career_status || 'invisible');
      console.debug('🔍 CVUpload.tsx - base_locations:', cvMetadata.base_locations?.trim() || '(empty)');
      console.debug('🔍 CVUpload.tsx - preferred_locations:', cvMetadata.preferred_locations?.trim() || '(empty)');
      console.debug('🔍 CVUpload.tsx - role_preferences:', cvMetadata.role_preferences?.trim() || '(empty)');
      console.debug('🔍 CVUpload.tsx - is_contactable:', cvMetadata.is_contactable || false);

      // 🔒 GDPR COMPLIANCE: Record consent BEFORE upload
      // This ensures we have documented proof of consent before processing CV
      console.debug('📋 Recording consent before upload (GDPR requirement)...');
      try {
        await recordConsent('cv_upload_full_consent', true);
        console.debug('✅ Consent recorded successfully - proceeding with upload');
      } catch (error) {
        console.error('❌ Failed to record consent:', error);
        throw new Error('Failed to record consent. Please try again or contact support if the issue persists.');
      }

      // Call API Gateway with authentication (port 8000) using apiCall for retry logic
      const response = await apiCall(`${process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000'}/api/v1/cvs/upload`, {
        method: 'POST',
        body: formData
      });

      if (!response.ok) {
        if (response.status === 401) {
          console.debug('Authentication failed, showing auth prompt');
          if (!isSignedIn) {
            setShowAuthModal(true);
          }
          setIsProcessing(false);
          setUploadStatus('error');
          return;
        }
        
        // Handle API errors with proper error handler
        const apiError = await handleApiError(response);
        
        if (apiError.status === 429 && apiError.rateLimitInfo) {
          // Show rate limit notification component
          setRateLimitInfo(apiError.rateLimitInfo);
          setIsProcessing(false);
          setUploadStatus('error');
          // Clear upload lock so user can retry after rate limit expires
          localStorage.removeItem('cv_upload_timestamp');
          return;
        }
        
        // Include status code in error message for better error handling
        throw new Error(`Upload failed (${response.status}): ${response.statusText}`);
      }

      const result = await response.json();
      console.debug('Upload queued:', result);
      
      // Handle queue response
      if (result.status === 'queued') {
        localStorage.setItem('cv_upload_timestamp', Date.now().toString());
        console.debug('✅ Upload queued successfully - cooldown lock enabled');
        
        setUploadStatus('queued');
        setTaskInfo({
          cv_id: result.cv_id,
          task_id: result.task_id,
          message: result.message
        });
        
        // Update existingCvId to new CV ID
        setExistingCvId(result.cv_id);

        if (onUploaded) {
          // Embedded (Passport modal): let the parent close + show the
          // "generating" state instead of the standalone success modal.
          onUploaded(result.cv_id);
        } else {
          // Show success modal with Real Talk nudge
          setShowUploadSuccessModal(true);
        }
        
        // Consent already recorded before upload (GDPR compliance)
      } else {
        // Fallback for non-queue response
        localStorage.setItem('cv_upload_timestamp', Date.now().toString());
        console.debug('✅ Upload successful - cooldown lock enabled');
        
        setUploadStatus('success');
        
        // Update existingCvId to new CV ID
        if (result.cv_id) {
          setExistingCvId(result.cv_id);
          if (onUploaded) onUploaded(result.cv_id);
        }
        
        // Consent already recorded before upload (GDPR compliance)
      }
      
    } catch (error: any) {
      console.error('Upload error:', error);
      setUploadStatus('error');
      
      // ❌ Upload failed - clear lock so user can retry immediately
      localStorage.removeItem('cv_upload_timestamp');
      console.debug('❌ Upload failed - lock cleared for immediate retry');
      
      // Show user-friendly error messages
      if (error.message && error.message.includes('503')) {
        showToast(t('errors.serviceUnavailable'), 'error');
      } else if (error.message && error.message.includes('504')) {
        showToast(t('errors.uploadTimeout'), 'error');
      } else {
        showToast(t('errors.uploadFailed'), 'error');
      }
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <div className={embedded ? '' : 'min-h-screen bg-gray-50 py-8'}>
      {/* Feature Block Modal */}
      {showBlockModal && (
        <FeatureBlockModal 
          featureName="CV Upload" 
          requiredUserType="explorer" 
          currentUserType="recruiter"
          onClose={() => setShowBlockModal(false)}
        />
      )}

      {/* Rate Limit Notification */}
      {rateLimitInfo && (
        <RateLimitNotification 
          rateLimitInfo={rateLimitInfo}
          feature="cv_upload"
          onClose={() => setRateLimitInfo(null)}
        />
      )}
      
      {/* In-App Browser Warning Banner - same style as Hire page */}
      {!embedded && inAppBrowser && (
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 mb-4">
          <div className="bg-gradient-to-br from-yellow-50 to-pink-100 p-4 rounded-lg border border-pink-200 shadow-sm">
            <div className="flex flex-col sm:flex-row items-center justify-between gap-3">
              <div className="flex items-center gap-2">
                <span className="text-lg">📱</span>
                <div>
                  <h3 className="text-sm font-semibold text-pink-800">
                    {i18n.language === 'de' 
                      ? `${inAppBrowser} erkannt - öffne in Chrome oder Safari für beste Erfahrung` 
                      : `${inAppBrowser} detected - open in Chrome or Safari for best experience`}
                  </h3>
                  <p className="text-sm text-pink-600">
                    {i18n.language === 'de' 
                      ? 'Kopiere die URL oder tippe ⋯ → "Im Browser öffnen"'
                      : 'Copy the URL or tap ⋯ → "Open in browser"'}
                  </p>
                </div>
              </div>
              <button 
                onClick={handleCopyUrl}
                className="px-3 py-1.5 text-sm bg-gradient-to-r from-pink-500 via-orange-400 to-yellow-400 text-white font-medium rounded-lg hover:from-pink-600 hover:via-orange-500 hover:to-yellow-500 transition-all shadow-sm whitespace-nowrap flex items-center gap-1.5"
              >
                {urlCopied ? (
                  <>
                    <Check size={14} />
                    {i18n.language === 'de' ? 'Kopiert!' : 'Copied!'}
                  </>
                ) : (
                  <>
                    <Copy size={14} />
                    {i18n.language === 'de' ? 'URL kopieren' : 'Copy URL'}
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      <div className={embedded ? '' : 'max-w-4xl mx-auto px-4 sm:px-6 lg:px-8'}>
        {/* Header */}
        {!embedded && (
        <div className="text-center mb-6 sm:mb-8">
          <h1 className="text-xl sm:text-[1.65rem] font-bold text-gray-900 leading-tight">
            {t('title')}
          </h1>
          {/* Value pills — mirrors the Practice/Explore tab hero so Inspire
              reads as part of the same consumer surface (no box/ratio chrome). */}
          <div className="mt-3 flex flex-wrap gap-1.5 justify-center">
            <span className="inline-flex items-center gap-1.5 text-[11px] font-semibold text-slate-700 bg-white border border-slate-200 px-2.5 py-1 rounded-full shadow-[0_1px_2px_rgba(15,23,42,0.04)]">
              <Sparkles className="w-3 h-3 text-pink-600" />
              {t('heroPills.passport')}
            </span>
            <span className="inline-flex items-center gap-1.5 text-[11px] font-semibold text-slate-700 bg-white border border-slate-200 px-2.5 py-1 rounded-full shadow-[0_1px_2px_rgba(15,23,42,0.04)]">
              <Heart className="w-3 h-3 text-pink-600" />
              {t('heroPills.connect')}
            </span>
            <span className="inline-flex items-center gap-1.5 text-[11px] font-semibold text-slate-700 bg-white border border-slate-200 px-2.5 py-1 rounded-full shadow-[0_1px_2px_rgba(15,23,42,0.04)]">
              <Briefcase className="w-3 h-3 text-pink-600" />
              {t('heroPills.recruiters')}
            </span>
          </div>
        </div>
        )}

        {/* Status Messages — hide the queued banner while the success modal is
            open so we don't double up the same "we'll email you" message. The
            banner stays as the persistent page status once the modal is closed. */}
        {uploadStatus === 'queued' && !showUploadSuccessModal && (
          <div ref={statusRef} className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
            <div className="flex items-center mb-3">
              <CheckCircle className="text-blue-600 mr-3" size={20} />
              <span className="text-blue-800 font-medium">{t('status.queued')}</span>
            </div>
            <div className="text-blue-700 mb-3">
              {taskInfo.message || t('status.processing')}
            </div>
            <div className="bg-blue-100 p-3 rounded-lg">
              <div className="flex items-center mb-2">
                <Mail className="text-blue-600 mr-2" size={16} />
                <span className="text-blue-800 font-medium">{t('status.emailNotification')}</span>
              </div>
              <p className="text-blue-700 text-sm" dangerouslySetInnerHTML={{
                __html: t('status.emailDetails', { email: user?.email })
              }} />
              {taskInfo.cv_id && (
                <p className="text-blue-600 text-xs mt-2">
                  {t('status.cvId', { cvId: taskInfo.cv_id })}
                </p>
              )}
            </div>
          </div>
        )}
        
        {uploadStatus === 'success' && (
          <div ref={statusRef} className="mb-6 p-3 sm:p-4 bg-green-50 border border-green-200 rounded-lg flex items-center">
            <CheckCircle className="text-green-600 mr-2 sm:mr-3 flex-shrink-0" size={18} />
            <span className="text-sm sm:text-base text-green-800">{t('status.success')}</span>
          </div>
        )}
        
        {uploadStatus === 'error' && (
          <div ref={statusRef} className="mb-6 p-3 sm:p-4 bg-red-50 border border-red-200 rounded-lg flex items-center">
            <AlertCircle className="text-red-600 mr-2 sm:mr-3 flex-shrink-0" size={18} />
            <span className="text-sm sm:text-base text-red-800">{t('status.error')}</span>
          </div>
        )}

        {/* Main Content */}
        <div className={embedded ? '' : 'space-y-6'}>
          {/* Step 1: Upload & Anonymize CV */}
          <div className={embedded ? '' : 'card'}>
            <div className="mb-4">
              <div className="flex flex-col sm:flex-row sm:items-center mb-2 gap-2">
                <div className="flex items-center">
                  <h2 className={embedded
                    ? 'text-sm font-semibold text-slate-700'
                    : 'text-[12px] sm:text-lg font-semibold text-orange-600'}>
                    {t('anonymization.title')}
                  </h2>
                </div>
                
                {/* Existing CV Notice */}
                {existingCvId && (
                  <span className="px-1.5 py-0.5 sm:px-2 sm:py-1 text-[10px] sm:text-xs bg-green-100 text-green-800 rounded-full flex items-center gap-1 sm:ml-auto w-fit">
                    <CheckCircle size={12} />
                    {t('anonymization.existingCv')}{' '}
                    <Link 
                      to="/profile"
                      className="font-medium underline hover:text-green-900"
                    >
                      {t('anonymization.existingCvView')}
                    </Link>
                    {' '}{t('anonymization.existingCvUpdate')}
                  </span>
                )}
              </div>
              <p className={`text-[11px] sm:text-sm transition-all duration-300 ${
                showAnonymizationReminder 
                  ? 'relative z-[10003] bg-orange-100 text-orange-800 font-semibold px-3 py-2 rounded-lg animate-pulse border-2 border-orange-300 shadow-2xl' 
                  : 'text-gray-600'
              }`}>
                {t('anonymization.subtitle')}
              </p>
            </div>

            {/* File Upload Button — in the modal we hide the redundant chooser
                when no file is picked yet (the big dropzone below handles it). */}
            {(!embedded || uploadedFile) && (
              <div className="mb-4">
                <FileUpload 
                  onFileSelect={handleFileSelect}
                  selectedFile={uploadedFile}
                  isSignedIn={isSignedIn}
                  onAuthRequired={handleAuthRequired}
                  isBlocked={isBlocked}
                  onBlockedAttempt={() => setShowBlockModal(true)}
                />
              </div>
            )}

            {/* CV Editor */}
            {uploadedFile ? (
              <CVEditor
                file={uploadedFile}
                onPagesConverted={handlePagesConverted}
                convertedPages={convertedPages}
                currentPageIndex={currentPageIndex}
                onPageChange={setCurrentPageIndex}
                onUpload={handleUploadAnonymized}
                isProcessing={isProcessing}
                uploadStatus={uploadStatus}
                cvMetadata={cvMetadata}
                onMetadataChange={setCvMetadata}
                profileLoaded={profileLoaded}
                highlightAddBoxButton={showAnonymizationReminder}
                onAddBoxClick={() => setShowAnonymizationReminder(false)}
                hidePreferences={hidePreferences}
              />
            ) : (
              <div>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".pdf,.png,.jpg,.jpeg"
                  onChange={(e) => {
                    const files = e.target.files;
                    if (files && files.length > 0) {
                      handleFileSelect(files[0]);
                    }
                  }}
                  className="hidden"
                />
                <div 
                  className={`h-48 flex items-center justify-center rounded-lg border-2 border-dashed transition-colors cursor-pointer hover:border-gray-600 hover:bg-gray-100 ${
                    dragActive 
                      ? 'border-gray-600 bg-gray-100' 
                      : 'border-gray-300 bg-gray-50'
                  }`}
                  onClick={() => {
                    requireAuth(() => {
                      fileInputRef.current?.click();
                    });
                  }}
                  onDrop={handleDrop}
                  onDragOver={handleDragOver}
                  onDragEnter={handleDragEnter}
                  onDragLeave={handleDragLeave}
                >
                  <div className="text-center pointer-events-none px-2">
                    <Upload className={`mx-auto mb-2 sm:mb-3 ${dragActive ? 'text-gray-600' : 'text-gray-400'}`} size={32} />
                    <p className={`font-medium mb-1 text-xs sm:text-base ${dragActive ? 'text-gray-700' : 'text-gray-600'}`}>
                      {dragActive ? t('upload.dragActive') : t('upload.dragInactive')}
                    </p>
                    <p className="text-[10px] sm:text-sm text-gray-500">
                      {t('upload.formats')}
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Privacy & Compliance Info */}
            <div className={embedded
              ? 'mt-5 pt-4 border-t border-slate-100'
              : 'mt-6 p-4 sm:p-6 bg-white border border-gray-200 rounded-lg'}>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 sm:gap-6 max-w-5xl mx-auto">
                <div className="flex flex-col items-center text-center">
                  <UserX className={embedded ? 'text-slate-400 mb-1.5' : 'text-slate-700 mb-1.5 sm:mb-2'} size={16} />
                  <p className="text-[10px] sm:text-xs text-slate-400 font-medium leading-tight">{t('privacy.noSell')}</p>
                </div>
                <div className="flex flex-col items-center text-center">
                  <Shield className={embedded ? 'text-slate-400 mb-1.5' : 'text-slate-700 mb-1.5 sm:mb-2'} size={16} />
                  <p className="text-[10px] sm:text-xs text-slate-400 font-medium leading-tight">{t('privacy.gdpr')}</p>
                </div>
                <div className="flex flex-col items-center text-center">
                  <Lock className={embedded ? 'text-slate-400 mb-1.5' : 'text-slate-700 mb-1.5 sm:mb-2'} size={16} />
                  <p className="text-[10px] sm:text-xs text-slate-400 font-medium leading-tight">{t('privacy.noStorage')}</p>
                </div>
                <div className="flex flex-col items-center text-center">
                  <Brain className={embedded ? 'text-slate-400 mb-1.5' : 'text-slate-700 mb-1.5 sm:mb-2'} size={16} />
                  <p className="text-[10px] sm:text-xs text-slate-400 font-medium leading-tight">{t('privacy.noTraining')}</p>
                </div>
              </div>
            </div>
          </div>

        </div>


        {/* Authentication Modal */}
        <AuthModal 
          isOpen={showAuthModal}
          onClose={() => setShowAuthModal(false)}
          signupPath="/signup"
        />

        {/* Anonymization Reminder Overlay - Blur background only */}
        {showAnonymizationReminder && (
          <div 
            className="fixed inset-0 bg-black/50 z-[10001]"
            onClick={() => setShowAnonymizationReminder(false)}
          />
        )}

        {/* Anonymization Confirmation Modal */}
        <AnonymizationConfirmModal
          isOpen={showConfirmModal}
          onClose={() => {
            setShowConfirmModal(false);
            setPendingUpload(null);
          }}
          onConfirm={confirmAndUpload}
        />

        {/* Upload Success Modal with Real Talk Nudge */}
        <UploadSuccessModal
          isOpen={showUploadSuccessModal}
          onClose={() => setShowUploadSuccessModal(false)}
          email={user?.email}
          cvId={taskInfo.cv_id}
        />
      </div>
    </div>
  );
};

export default CVUpload;
